import React from 'react';
import { Check, Gift, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatMoney } from '../../lib/billingApi';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

export const PLAN_FEATURES = [
  'Unlimited publications, folders and templates',
  'Print-ready PDF, PNG and JPEG export',
  'Publisher (.pub) import and conversion',
  'Your own fonts, colour schemes and font schemes',
  'Share links, building blocks and a 30-day Trash',
  'Works on desktop, tablet and phone',
];

/**
 * One subscription plan on the subscribe page. `plan` comes from GET /billing/config ({ id, label, amount, currency,
 * interval, friend_discount }); `referred` switches the copy to the invited-friend perk; `trial` says whether a free
 * trial is still available to this account (one per account).
 */
export const PlanCard = ({ plan, featured = false, referred = false, trial = true, trialDays = 14, busy = false, disabled = false, onStart }) => {
  const price = formatMoney(plan.amount, plan.currency);
  const per = plan.interval === 'year' ? 'year' : 'month';
  const monthlyEquivalent = plan.interval === 'year' ? formatMoney(Math.round(plan.amount / 12), plan.currency) : null;
  const perk = referred
    ? plan.interval === 'year'
      ? { title: '$5 off your first year', text: `A friend invited you — your first year is ${formatMoney(plan.amount - 500, plan.currency)} instead of ${price}.` }
      : { title: 'Your first month is free', text: 'A friend invited you — the first month after your trial is on us.' }
    : null;
  const afterTrial = trial ? `${trialDays} days free, then ${price} a ${per}.` : `${price} a ${per}, billed today.`;

  return (
    <Card className={`relative flex flex-col bg-card ${featured ? 'border-primary/60 ring-1 ring-primary/30 shadow-md' : 'shadow-sm'}`} data-testid={`plan-card-${plan.id}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold" style={heading}>{plan.label}</CardTitle>
          {featured && <Badge className="bg-primary text-primary-foreground hover:bg-primary" data-testid={`plan-badge-${plan.id}`}>Best value · save 16%</Badge>}
        </div>
        <div className="flex items-baseline gap-1.5 pt-1">
          <span className="text-4xl font-semibold tracking-tight tabular-nums" style={heading} data-testid={`plan-price-${plan.id}`}>{price}</span>
          <span className="text-sm text-muted-foreground">/ {per}</span>
          {monthlyEquivalent && <span className="ml-auto text-xs text-muted-foreground">≈ {monthlyEquivalent} / month</span>}
        </div>
        <CardDescription className="text-sm" data-testid={`plan-terms-${plan.id}`}>{afterTrial} Cancel any time.</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        {perk && (
          <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs flex items-start gap-2" data-testid={`plan-perk-${plan.id}`}>
            <Gift className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" />
            <span><span className="font-semibold text-foreground">{perk.title}.</span> <span className="text-muted-foreground">{perk.text}</span></span>
          </div>
        )}
        <ul className="space-y-2 text-sm">
          {PLAN_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>{f}</span></li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="pt-2">
        <Button type="button" className="w-full h-11 text-sm font-semibold" variant={featured ? 'default' : 'outline'} onClick={() => onStart(plan.id)} disabled={busy || disabled} data-testid={`plan-start-${plan.id}`}>
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />Opening secure checkout…</> : trial ? `Start ${trialDays}-day free trial` : `Subscribe — ${price} / ${per}`}
        </Button>
      </CardFooter>
    </Card>
  );
};
